import type { ComponentType } from 'react'

type ModuleWithDefault<T extends ComponentType<unknown>> = {
  default: T
}

function readFlag(key: string): boolean {
  try {
    return globalThis.sessionStorage?.getItem(key) === '1'
  } catch {
    return false
  }
}

function writeFlag(key: string, value: '1' | null) {
  try {
    if (!globalThis.sessionStorage) return
    if (value === null) {
      globalThis.sessionStorage.removeItem(key)
      return
    }
    globalThis.sessionStorage.setItem(key, value)
  } catch {
    // Ignore storage access issues (e.g., restricted mode)
  }
}

function isDynamicImportChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk/i.test(error.message)
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  importer: () => Promise<ModuleWithDefault<T>>,
  moduleId: string
): Promise<ModuleWithDefault<T>> {
  const retryKey = `tolucal_lazy_retry_${moduleId}`

  return importer()
    .then((module) => {
      writeFlag(retryKey, null)
      return module
    })
    .catch((error: unknown) => {
      if (typeof globalThis.window !== 'undefined' && isDynamicImportChunkError(error)) {
        if (!readFlag(retryKey)) {
          writeFlag(retryKey, '1')
          globalThis.window.location.reload()
          return new Promise<ModuleWithDefault<T>>(() => {})
        }
      }

      throw error
    })
}
